import { WSCard, WSText } from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { PayableBulkActionType } from "@wingspanhq/wingspan-api-types/dist/src/generated/paymentsApiSpec";
import { useWSMutation } from "@ws-react-query";
import React, { useEffect, useState } from "react";
import { useQueryListPayablesBulkActionBatches } from "../../../../query/payables/useQueryListPayablesBulkActionBatches";
import {
  createPayableBulkAction,
  listPayables,
  Payable,
  PayablesSummaryResponse
} from "../../../../services/api/payments/payables";
import { useDefaultCurrency } from "../../../../shared/hooks/useDefaultCurrency";
import {
  BulkActionProgressBanner,
  bulkActionFactory,
  useForegroundBulkAction
} from "../../../BulkActions";
import { useModalBackgroundBulkAction } from "../../../BulkActions/modals/ModalBackgroundBulkAction";
import {
  invalidatePayablesAfterMutation,
  PAYABLE_BULK_ACTION_CONFIG
} from "../../utils/payableBulkActionConfig";

const MIN_ITEMS_FOR_BULK_ACTION = 2;
// At or below this count the action runs on the FE, one request per payable.
// Above it, we hand the whole result set to the backend batch endpoint.
const MAX_ITEMS_FOR_FE_BULK_ACTION = 100;
const POLLING_INTERVAL = 1000;

type Props = {
  action: PayableBulkActionType;
  summary?: PayablesSummaryResponse;
  resultSetId?: string;
};

// Pages the existing materialized result set by id alone — the filter/sort are
// baked into the set at materialization time, so the request isn't needed here.
// If the set has expired (15-min sliding TTL), the backend 404s rather than
// re-materializing, so this fails cleanly instead of acting on a different set.
const fetchAllMatchingPayables = async (
  resultSetId: string
): Promise<Payable[]> => {
  const collected: Payable[] = [];
  let token = "";

  // The FE path is gated to <= MAX_ITEMS_FOR_FE_BULK_ACTION, so this only ever
  // walks a couple of pages before nextPageToken runs out.
  do {
    const page = await listPayables({
      "page[mode]": "materialized",
      "page[size]": MAX_ITEMS_FOR_FE_BULK_ACTION,
      "page[resultSetId]": resultSetId,
      ...(token ? { "page[token]": token } : {})
    });
    collected.push(...page.items);
    token = page.nextPageToken ?? "";
  } while (token);

  return collected;
};

export const FeaturedBulkAction: React.FC<Props> = props => {
  const defaultCurrency = useDefaultCurrency();
  const modalBackgroundBulkAction = useModalBackgroundBulkAction();
  const runForegroundBulkAction = useForegroundBulkAction();
  const [isRunningOnFe, setIsRunningOnFe] = useState(false);

  const config = PAYABLE_BULK_ACTION_CONFIG[props.action];
  const count = props.summary?.listSize ?? 0;

  const queryInProgressBatches = useQueryListPayablesBulkActionBatches(
    {
      "filter[actionType][=]": props.action,
      "filter[status][in]": ["Pending", "Processing"]
    },
    {
      refetchInterval: data => {
        if (!data || data.length > 0) {
          return POLLING_INTERVAL;
        }
        return false;
      }
    }
  );

  useEffect(() => {
    invalidatePayablesAfterMutation([]);
  }, [queryInProgressBatches.data?.length]);

  const inProgressBatches = queryInProgressBatches.data ?? [];

  // Above the threshold: hand the whole result set to the backend batch job.
  const [createBatch, createBatchMeta] = useWSMutation(
    async () => {
      const confirmed = await modalBackgroundBulkAction.open({
        actionName: config.actionName,
        actionType: "background",
        itemCount: count
      });

      if (!confirmed) {
        return;
      }

      return createPayableBulkAction({
        actionType: props.action,
        resultSetId: props.resultSetId!
      });
    },
    {
      onSuccess: () => {
        queryInProgressBatches.refetch();
      }
    }
  );

  // At or below the threshold: process every matching payable on the FE, one
  // request each, with the foreground confirm → progress → results modals.
  const runOnFe = async () => {
    if (!props.resultSetId) {
      return;
    }

    setIsRunningOnFe(true);
    try {
      await runForegroundBulkAction<Payable[]>({
        actionName: config.actionName,
        itemCount: count,
        confirmLabel: config.confirmLabel,
        confirmDescription: config.confirmDescription(count),
        confirmDestructive: config.confirmDestructive,
        prepare: () => fetchAllMatchingPayables(props.resultSetId!),
        getItemCount: items => items.length,
        process: async (items, onProgress) => {
          const applicable = config.isApplicable
            ? items.filter(config.isApplicable)
            : items;
          const skipped = items.length - applicable.length;

          const bulkAction = bulkActionFactory<Payable>({
            onProgress,
            action: config.applyToItem,
            onFinish: ({ successItems }) => {
              invalidatePayablesAfterMutation(successItems);
            }
          });

          const { successItems, failedItems } =
            await bulkAction.run(applicable);

          return {
            total: items.length,
            processed: successItems.length + failedItems.length + skipped,
            successful: successItems.length,
            failed: failedItems.length
          };
        }
      });
    } finally {
      setIsRunningOnFe(false);
    }
  };

  if (inProgressBatches.length > 0) {
    return (
      <>
        {inProgressBatches.map(batch => (
          <BulkActionProgressBanner
            key={batch.bulkPayablesActionBatchId}
            title={PAYABLE_BULK_ACTION_CONFIG[batch.actionType].actionName}
            isAwaitingProcessing={batch.status === "Pending"}
            progress={{
              total: batch.payableStatistics?.itemsTotal || 0,
              processed: batch.payableStatistics?.itemsProcessed || 0,
              successful: batch.payableStatistics?.itemsProcessed || 0,
              failed: batch.payableStatistics?.itemsFailed || 0
            }}
          />
        ))}
      </>
    );
  }

  if (count < MIN_ITEMS_FOR_BULK_ACTION || !props.resultSetId) {
    return null;
  }

  const total = props.summary?.totalValue;
  const runOnBackend = count > MAX_ITEMS_FOR_FE_BULK_ACTION;

  return (
    <WSCard
      header={{ label: { text: `${config.cardHeader}:` } }}
      divider="bottom"
      actions={{
        alignment: "left",
        buttons: [
          {
            label: config.actionName,
            kind: "Link",
            rightIcon: "chevron-right",
            onClick: () => {
              if (runOnBackend) {
                createBatch();
              } else {
                runOnFe();
              }
            },
            loading: createBatchMeta.isLoading || isRunningOnFe
          }
        ]
      }}
    >
      <WSText.ParagraphSm color="gray500">
        {count.toLocaleString()} payables
        {total !== undefined && (
          <> totaling {formatMoney(total, defaultCurrency)}</>
        )}
      </WSText.ParagraphSm>
    </WSCard>
  );
};
